/**
 * Shared sentinels + service-worker-control helpers for the demo MSW lifecycle
 * (WI 65929). These string keys and the control predicate were previously
 * duplicated as magic literals across `main.ts` and `mocks/browser.ts`; the
 * "have we reloaded once for SW control" contract spanned both files coupled
 * only by a raw string, so a change needed lockstep edits (Shotgun Surgery).
 *
 * Deliberately DEPENDENCY-FREE: `main.ts` imports this in EVERY build (including
 * the clean ci/test/production bundles), so it must never pull in MSW/mock/demo
 * code. It holds only string constants + trivial predicates — none of which are
 * clean-bundle sentinels (see tools/assert-clean-bundle.mjs).
 */

/** One-shot sessionStorage guard: main.ts reloaded once to gain SW control. */
export const DEMO_MSW_RELOAD_GUARD = 'sgDemoMswReloaded';

/**
 * One-shot sessionStorage guard: main.ts reloaded once after tearing down a
 * stale demo service worker on a non-demo load.
 */
export const DEMO_TEARDOWN_RELOAD_GUARD = 'sgDemoTeardownReloaded';

/**
 * localStorage `access_token` value the demo auth-bypass sets. Used to
 * distinguish a stale demo session from a real one — never clear a real token.
 */
export const MOCK_ACCESS_TOKEN = 'mock-access-token';

/** Substring identifying the MSW worker by its scriptURL (vs a real/PWA worker). */
export const MOCK_SW_SCRIPT_MARKER = 'mockServiceWorker';

/** True when the service worker API is available in this environment. */
export function isServiceWorkerSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.serviceWorker;
}

/**
 * True when a service worker currently CONTROLS this page. MSW can only
 * intercept requests (handlers AND onUnhandledRequest) once this is true — an
 * uncontrolled page bypasses MSW entirely and hits the real network.
 */
export function isPageControlled(): boolean {
  return isServiceWorkerSupported() && !!navigator.serviceWorker.controller;
}
